import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShoppingBag,
  TrendingUp,
  Clock,
  DollarSign,
  Package,
  ArrowRight,
} from "lucide-react";
import { adminStats } from "../../services/adminApi";
import { RefillReminderToggle } from "./RefillReminderToggle";

const STATUS_LABELS = {
  nueva: "Nueva",
  preparando: "Preparando",
  enviado: "Enviado",
  entregado: "Entregado",
  cancelado: "Cancelado",
};

const STATUS_COLORS = {
  nueva: "bg-blue-100 text-blue-800",
  preparando: "bg-amber-100 text-amber-800",
  enviado: "bg-purple-100 text-purple-800",
  entregado: "bg-green-100 text-green-800",
  cancelado: "bg-red-100 text-red-800",
};

const fmt = (n) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
  }).format(n || 0);

const StatCard = ({ icon: Icon, label, value, color = "amber", testid }) => (
  <div
    className="bg-white rounded-2xl p-6 border border-stone-200 hover:shadow-md transition-shadow"
    data-testid={testid}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-stone-500 font-medium">{label}</p>
        <p className="text-3xl font-bold text-stone-800 mt-2">{value}</p>
      </div>
      <div className={`p-3 rounded-xl bg-${color}-50`}>
        <Icon className={`w-5 h-5 text-${color}-700`} />
      </div>
    </div>
  </div>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminStats
      .get()
      .then(setStats)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-1/3 bg-stone-200 rounded" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-stone-200 rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-dashboard">
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-stone-800">¡Hola, Mariana! 🍯</h1>
        <p className="text-stone-500 mt-1">
          Aquí está el resumen de tu tienda hoy.
        </p>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={ShoppingBag}
          label="Pedidos hoy"
          value={stats.today_orders}
          color="amber"
          testid="stat-today-orders"
        />
        <StatCard
          icon={Clock}
          label="Por preparar/enviar"
          value={stats.pending_orders}
          color="orange"
          testid="stat-pending"
        />
        <StatCard
          icon={DollarSign}
          label="Ventas este mes"
          value={fmt(stats.revenue_month)}
          color="green"
          testid="stat-revenue-month"
        />
        <StatCard
          icon={TrendingUp}
          label="Ventas esta semana"
          value={fmt(stats.revenue_week)}
          color="blue"
          testid="stat-revenue-week"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top products */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-stone-800">
              Productos más vendidos
            </h2>
            <span className="text-xs text-stone-400">Últimos 30 días</span>
          </div>
          {stats.top_products.length === 0 ? (
            <p className="text-stone-400 text-sm py-8 text-center">
              Aún no hay ventas registradas
            </p>
          ) : (
            <ul className="space-y-3">
              {stats.top_products.map((p, idx) => (
                <li
                  key={p.name}
                  className="flex items-center justify-between p-3 bg-stone-50 rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-amber-100 text-amber-800 rounded-full flex items-center justify-center font-bold text-sm">
                      {idx + 1}
                    </div>
                    <div>
                      <p className="font-medium text-stone-800 text-sm">
                        {p.name}
                      </p>
                      <p className="text-xs text-stone-500">
                        {p.quantity} unidades
                      </p>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-stone-700">
                    {fmt(p.revenue)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Status breakdown */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-stone-800">
              Estado de pedidos
            </h2>
            <Link
              to="/admin/orders"
              className="text-sm text-amber-700 hover:text-amber-800 flex items-center gap-1"
            >
              Ver todos <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {Object.keys(stats.status_breakdown).length === 0 ? (
            <p className="text-stone-400 text-sm py-8 text-center">
              Sin pedidos aún
            </p>
          ) : (
            <div className="space-y-2">
              {Object.entries(stats.status_breakdown).map(([status, count]) => (
                <div
                  key={status}
                  className="flex items-center justify-between p-3 bg-stone-50 rounded-xl"
                >
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      STATUS_COLORS[status] || "bg-stone-200 text-stone-700"
                    }`}
                  >
                    {STATUS_LABELS[status] || status}
                  </span>
                  <span className="font-semibold text-stone-700">{count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 bg-gradient-to-r from-amber-50 to-stone-50 border border-amber-100 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-amber-700 rounded-xl flex items-center justify-center">
            <Package className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-semibold text-stone-800">
              Total de pedidos: {stats.total_orders}
            </p>
            <p className="text-sm text-stone-500">
              Desde el inicio de la tienda
            </p>
          </div>
        </div>
        <Link
          to="/admin/orders"
          className="px-5 py-2.5 bg-amber-700 hover:bg-amber-800 text-white rounded-xl text-sm font-medium transition-colors"
        >
          Gestionar pedidos
        </Link>
      </div>
      <RefillReminderToggle />
    </div>
  );
}
